/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, CommonActions, CommonFlowState, ListActionPipelines } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { EntityInputDialogConfig } from '@gh/common-internal-shared';
import { getRouteInitData, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService } from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { CommunityListBusinessService } from '../../resources/community-list-business.service';
import { Community } from '../../resources/community.model';
import { communityDetailsDialog } from './community-details-dialog.route';

@Component({
  selector: 'gh-community-list-tab',
  template: require('./community-list-tab.component.html'),
  styles: [require('./community-list-tab.component.scss')],
})
export class CommunityListTabComponent implements OnInit {
  deletedToggle: boolean = false;
  communities: Community[];

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private navigationService: BuilderNavigationService,
              private communityBusiness: CommunityListBusinessService) {

  }

  /**
   * Init component
   */
  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));
  }

  onChangeDeleted(): void {
    this.reload();
  }

  onDelete(community: Community): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_community_delete',
      execute: this.commonActions.execute((state: CommonFlowState<Community>) =>
        this.communityBusiness.toggleStatus(state.value)),
      finish: () => this.reload(),
    });

    flow.run({
      value: community,
    });
  }

  onUnDelete(community: Community): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_community_un_delete',
      execute: this.commonActions.execute((state: CommonFlowState<Community>) =>
        this.communityBusiness.toggleStatus(state.value)),
      finish: () => this.reload(),
    });

    flow.run({
      value: community,
    });
  }

  onCreate(): void {
    this.dialogService.openWithInput(communityDetailsDialog, <EntityInputDialogConfig<Community>>{
      titleId: 'lbl_community',
      messageId: 'lbl_community_create',
      placeholderId: 'lbl_community_name',
      field: 'name',
      required: true,
      maxLength: 128,
      permissionEdit: BuilderPermission.Setup.Sales.Communities.CREATE,
      onSave: (community: Community) => this.communityBusiness.create(community),
    }).afterClosed().pipe(
      filter$((community) => !!community))
      .subscribe(() => {
        this.notificationService.success('msg_created_successfully');
        this.reload();
      });
  }

  onEdit({ id, name }: Community): void {
    if (this.userContext.hasPermission(BuilderPermission.Setup.Sales.Communities.EDIT)) {
      this.dialogService.openWithInput(communityDetailsDialog, <EntityInputDialogConfig<Community>>{
        titleId: 'lbl_community',
        titleParams: { 'community_name': name },
        messageId: 'lbl_community_edit',
        messageParams: { 'community_name': name },
        placeholderId: 'lbl_community_name',
        field: 'name',
        required: true,
        maxLength: 128,
        permissionEdit: BuilderPermission.Setup.Sales.Communities.EDIT,
        id,
        onSave: (community: Community) => this.communityBusiness.update(community),
      }).afterClosed().pipe(
        filter$(Boolean))
        .subscribe(() => {
          this.notificationService.success('msg_saved_successfully');
          this.reload();
        });
    }
  }

  reload(): void {
    this.communityBusiness.findAll(!this.deletedToggle).pipe(
      (this.waitingService.operator()))
      .subscribe((communities) => this.communities = communities);
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      label: label ? label : 'lbl_menu_communities',
      entityType: entityType ? entityType : 'COMMUNITY',
      entityId: entityId ? entityId : undefined,
    });
  }
}
